import { isBefore, isAfter, isEqual } from 'date-fns'

import './types'
import { tempPrescribedWorkingHoursMonth, tempEmploymentTypeSetting } from './store'
import { parseDateString } from '../../../../common/utils/DateUtils'

/*==================================
  store.js のヘルパー関数
===================================*/
/**
 * 与えられたデータ配列を指定されたキー（年度など）でグループ化するヘルパー関数。
 *
 * @param {Object[]} dataList - グループ化するオブジェクトの配列。
 * @param {string} key - グループ化の基準となるプロパティ名。デフォルトは 'fiscalYear'。
 * @returns {Object<string, Object[]>} 年度をキーとして、関連するデータ配列を持つオブジェクト。
 */
export const groupDataByFiscalYear = (dataList, key = 'fiscalYear') => {
  if (!Array.isArray(dataList)) {
    console.error(`groupDataByFiscalYear: データリストに指定キー「${key}」のアイテムが存在しません`)
    return {}
  }
  return dataList.reduce((acc, currentItem) => {
    const groupKey = currentItem[key]
    if (groupKey) {
      if (!acc[groupKey]) {
        acc[groupKey] = []
      }
      acc[groupKey].push(currentItem)
    }
    return acc
  }, {})
}

/**
 * リデューサー内で指定された年度の設定情報取得関数。
 *
 * @param {object} draft - Immerのドラフト状態。
 * @param {string} selectedFiscalYear - 選択された年度。
 * @returns {object | null} 該当年度の設定オブジェクト、またはnull。
 */
export const getFiscalYearSettings = (draft, selectedFiscalYear) => {
  return draft?.employeeCountInfo?.fiscalYearSettings?.[selectedFiscalYear] || null
}

/**
 * 指定された accPeriod の翌月から始まり、12ヶ月分の所定労働時間（月）テンプレートを生成する関数
 *
 * @param {string} accPeriod - 決算月（例："3月", "12月"）
 * @param {string} fiscalYear - 対象年度（例："2023年度"）
 * @param {string} fiscalYearMonth - 対象年度の決算年月（例："2024-03-31"）
 * @returns {Object[]} 12ヶ月分のテンプレート配列
 */
export function createPrescribedWorkingHoursMonthTemplate(accPeriod, fiscalYear, fiscalYearMonth) {
  // 月の数字を取得
  const accMonth = parseInt(accPeriod.replace('月', ''), 10)

  // 月名の配列（1月～12月）
  const monthNames = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月']

  // 開始月は accMonth の翌月（12月の場合は1月）
  let startMonthIndex = accMonth % 12 // 0-based index

  // 12ヶ月分の配列を作成
  const template = []
  for (let i = 0; i < 12; i++) {
    const monthIndex = (startMonthIndex + i) % 12
    const baseItem = tempPrescribedWorkingHoursMonth[0]
    template.push({
      ...baseItem,
      fiscalYear,
      fiscalYearMonth,
      month: monthNames[monthIndex]
    })
  }

  return template
}

/**
 * 雇用区分マスター、雇用区分設定から 有効な雇用区分設定情報を生成する
 *
 * @param {Object[]} employmentTypeMaster - 雇用区分マスター配列 各要素に employmentType, validStartDate, validEndDate, displayOrder
 *   などを含む
 * @param {Object[]} employmentTypeSettingList - ある年度の雇用区分設定配列 各要素に employmentType, securitiesEmployeeType,
 *   fteEstimatedFactor 等を含む
 * @param {string} fiscalYear - 対象年度（例："2023年度"）
 * @param {string} fiscalYearStartMonth - 対象年度の期首日（例："2023-04-01"）
 * @param {string} fiscalYearMonth - 対象年度の決算年月（例："2024-03-31"）
 * @returns {Object[]} TempEmploymentTypeSetting 形式の配列
 */
export function generateEmploymentTypeSettings(
  employmentTypeMaster,
  employmentTypeSettingList,
  fiscalYear,
  fiscalYearStartMonth,
  fiscalYearMonth
) {
  // 無期限判定（空欄の場合は無期限とする）
  const toComparableDate = (date, isStart) => {
    if (!date) {
      // 開始日がnullなら過去の十分古い日付、終了日がnullなら未来の十分未来の日付とみなす
      return isStart ? new Date('1900-01-01') : new Date('9999-12-31')
    }
    return date
  }

  // 日付文字列をDateオブジェクトに変換
  const targetStartDate = parseDateString(fiscalYearStartMonth)
  const targetEndDate = parseDateString(fiscalYearMonth)

  // 雇用区分マスターから有効期間内のものを抽出
  const validEmploymentTypeMasters = employmentTypeMaster.filter((item) => {
    // 決算年月が存在しない場合は、すべて表示
    if (!targetStartDate || !targetEndDate) return false

    // 雇用区分の有効期限を取得
    const startDate = item.validStartDate ? parseDateString(item.validStartDate) : null
    const endDate = item.validEndDate ? parseDateString(item.validEndDate) : null

    // 有効日付が空欄の場合は、無期限とする
    const compStartDate = toComparableDate(startDate, true)
    const compEndDate = toComparableDate(endDate, false)

    // 有効判定条件：選択年度開始日 <= 雇用区分終了日 かつ 選択年度終了日 >= 雇用区分開始日
    // isBefore(a,b): a < b
    // isAfter(a,b): a > b
    // isEqual(a,b): a == b
    const startCondition = isBefore(targetStartDate, compEndDate) || isEqual(targetStartDate, compEndDate)
    const endCondition = isAfter(targetEndDate, compStartDate) || isEqual(targetEndDate, compStartDate)

    return startCondition && endCondition
  })

  // 結果配列
  const template = []
  const employmentTypeSet = new Set()

  validEmploymentTypeMasters.forEach((master) => {
    const { employmentType } = master

    // 重複チェック（重複している雇用区分が存在する場合はスキップ）
    if (employmentTypeSet.has(employmentType)) {
      return
    }
    // 抽出済みの雇用区分を保持
    employmentTypeSet.add(employmentType)

    const baseItem = tempEmploymentTypeSetting[0]

    // 雇用区分設定からemploymentTypeで該当を抽出（1件想定）
    const setting = employmentTypeSettingList.find((s) => s.employmentType === employmentType)

    template.push({
      ...baseItem,
      fiscalYear,
      fiscalYearMonth,
      employmentType: employmentType,
      securitiesEmployeeType: setting ? setting.securitiesEmployeeType : '',
      fteEstimatedFactor: setting ? Number(setting.fteEstimatedFactor) || null : null,
      fteActualWorkingHoursCalcMethod: setting ? setting.fteActualWorkingHoursCalcMethod : ''
    })
  })

  return template
}

/**
 * 配列を指定したキーを用いてMapに変換するヘルパー関数
 *
 * @param {Object[]} array - 変換対象の配列
 * @param {string} keyName - Mapのキーに利用するオブジェクトのプロパティ名
 * @returns {Map<any, Object>} 指定キーをキーとしたMapオブジェクト
 */
export function arrayToMap(array, keyName) {
  const map = new Map()
  array.forEach((item) => {
    map.set(item[keyName], item)
  })
  return map
}

/**
 * オブジェクトの特定のフィールド群について、差分があれば更新するヘルパー関数
 *
 * @param {Object} targetObj - 更新対象のオブジェクト
 * @param {Object} sourceObj - 元データのオブジェクト
 * @param {string[]} keys - 更新対象のフィールド名の配列
 */
export function copyChangedFields(targetObj, sourceObj, keys) {
  keys.forEach((key) => {
    if (targetObj[key] !== sourceObj[key]) {
      targetObj[key] = sourceObj[key]
    }
  })
}

/*==================================
  PrescribedWorkingHoursMonthSetting.js のヘルパー関数
===================================*/

/**
 * 決算月に基づいて配列を並び替える関数
 *
 * @param {Object[]} data - 12ヶ月分の配列
 * @param {string} accMonth - 決算月（例："3月"）
 * @returns {Object[]} - 並び替えられた配列
 */
export function reorderByAccMonth(data, accMonth) {
  const monthOrder = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月']
  const accIndex = monthOrder.indexOf(accMonth)
  if (accIndex === -1) return data

  const startIndex = (accIndex + 1) % 12

  const reorderedMonths = monthOrder.slice(startIndex).concat(monthOrder.slice(0, startIndex))

  return reorderedMonths.map((monthName) => {
    const found = data.find((item) => item.month === monthName)
    return {
      month: monthName,
      workingDays: found?.workingDays ?? '',
      prescribedWorkingHoursPerDay: found?.prescribedWorkingHoursPerDay ?? '',
      prescribedWorkingHoursPerMonth: found?.prescribedWorkingHoursPerMonth ?? ''
    }
  })
}

/*==================================
  EmployeeCountInfo.js のヘルパー関数
===================================*/
/**
 * 複数のエラーオブジェクトをフィールド単位でマージする関数。
 *
 * 各エラーオブジェクトは、以下の形式の連想配列であることを想定： { [fieldName: string]: { messages: string[], targets: string[] } }
 *
 * @param {...Object} errorObjects - マージ対象のエラーオブジェクトを可変長引数で受け取る
 * @returns {Object} マージ済みのエラーオブジェクト
 */
export function mergeErrors(...errorObjects) {
  const merged = {}

  errorObjects.forEach((errors) => {
    Object.entries(errors).forEach(([fieldName, { messages, targets }]) => {
      if (!merged[fieldName]) {
        // 初回はそのままセット
        merged[fieldName] = {
          messages: [...messages],
          targets: [...targets]
        }
      } else {
        // 既存があれば配列を連結
        merged[fieldName].messages = merged[fieldName].messages.concat(messages)
        merged[fieldName].targets = merged[fieldName].targets.concat(targets)
      }
    })
  })

  return merged
}
